import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { T } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Info, ArrowRight, GraduationCap, FileText, Sparkles } from "lucide-react";

export default function Landing() {
  const { user, lang } = useAuth();
  const t = T[lang];
  const navigate = useNavigate();

  const startFlow = () => navigate(user ? "/dashboard" : "/auth");

  const chips = {
    tr: ["Eğitim", "Projeler", "Staj & İş", "Spor", "Müzik", "Diller", "Dijital Yetkinlikler", "Sertifikalar", "Liderlik", "Ödüller", "Yayınlar", "Gönüllülük"],
    en: ["Education", "Projects", "Internship & Work", "Sports", "Music", "Languages", "Digital Skills", "Certificates", "Leadership", "Awards", "Publications", "Volunteering"],
  }[lang];

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1a2e4a] text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-in">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#254066] px-3 py-1 text-[11px] uppercase tracking-widest text-[#f3c9c4] mb-5">
              <GraduationCap className="h-3.5 w-3.5" /> {lang === "tr" ? "Üniversite & Yüksek Lisans Başvuruları" : "University & Graduate Applications"}
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.heroTitle}
            </h1>
            <p className="mt-5 text-[#c8d4e6] text-base sm:text-lg max-w-xl leading-relaxed">{t.heroDesc}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-start-btn"
                onClick={startFlow}
                className="bg-[#c0392b] hover:bg-[#a93226] text-white h-12 px-7 text-base rounded-full"
              >
                {t.start} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-2 max-w-xl">
              {chips.map((c) => (
                <span key={c} className="text-[11px] rounded-full border border-[#2c4366] px-3 py-1 text-[#9db4d6]">
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="absolute -inset-4 bg-[#c0392b]/20 blur-3xl rounded-full" />
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_uni-cv-assistant/artifacts/2a6f15230575271a_Student%20Building%20a%20CV%20Abroad%20chatty%20image.png"
              alt="Dual Study Abroad CV asistanını dolduran öğrenci"
              className="relative rounded-2xl shadow-2xl border-4 border-[#254066] object-cover w-full h-[420px]"
            />
          </div>
        </div>
      </section>

      {/* Info note */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10 relative z-10">
        <div data-testid="consultative-notice-banner" className="bg-white rounded-xl shadow-xl border border-[#e2e8f0] p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="shrink-0 h-11 w-11 rounded-full bg-[#fadbd8] flex items-center justify-center">
              <Info className="h-5 w-5 text-[#c0392b]" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl text-[#1a2e4a]">{t.infoTitle}</h2>
              <p className="mt-2 text-[#334155] leading-relaxed text-sm sm:text-base">{t.infoNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid sm:grid-cols-3 gap-6">
        {[
          { icon: Sparkles, tr: ["Soru-Cevap ile Topla", "Her kategoride sınırsız kayıt ekle; AI takip sorularıyla cevaplarını derinleştir."], en: ["Collect via Q&A", "Add unlimited records per category; deepen answers with AI follow-up questions."] },
          { icon: FileText, tr: ["Tablo Formatında CV", "Boş kategoriler dahil her başlık görünür — danışmanın eksikleri seninle konuşabilir."], en: ["Table-format CV", "Every section stays visible, even empty ones — so your advisor can review the gaps."] },
          { icon: GraduationCap, tr: ["İki Dilde PDF", "Aynı verilerle Türkçe ve İngilizce profesyonel PDF indir."], en: ["Bilingual PDF", "Download a professional PDF in Turkish and English from the same data."] },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#e2e8f0] p-6 hover:shadow-lg transition-shadow">
            <div className="h-11 w-11 rounded-lg bg-[#1a2e4a] flex items-center justify-center mb-4">
              <c.icon className="h-5 w-5 text-white" />
            </div>
            <h3 className="font-display font-bold text-lg text-[#1a2e4a]">{c[lang][0]}</h3>
            <p className="mt-2 text-sm text-[#334155] leading-relaxed">{c[lang][1]}</p>
          </div>
        ))}
      </section>

      <footer className="bg-[#1a2e4a] text-[#9db4d6] text-center py-6 text-xs">
        +44 7877 146 917 &nbsp;|&nbsp; info@dualstudyabroad.co.uk &nbsp;|&nbsp; www.dualstudyabroad.co.uk
      </footer>
    </div>
  );
}
